<script setup>
import { ref, onMounted } from "vue";
import { fetchStampLimits, updateStampLimits } from "./api";

const limits = ref([]);
const loading = ref(false);
const saving = ref(false);
const error = ref("");
const success = ref(false);
const showAddDialog = ref(false);
const newLimit = ref({ description: "", limit: 0 });

onMounted(async () => {
  loading.value = true;
  try { limits.value = await fetchStampLimits(); }
  catch { error.value = "Couldn't load stamp limits."; }
  finally { loading.value = false; }
});

function openAddDialog() {
  newLimit.value = { description: "", limit: 0 };
  showAddDialog.value = true;
}

function addLimit() {
  if (!newLimit.value.description) return;
  limits.value.push({ ...newLimit.value });
  showAddDialog.value = false;
}

function removeLimit(index) {
  limits.value.splice(index, 1);
}

async function save() {
  saving.value = true; error.value = ""; success.value = false;
  try { await updateStampLimits(limits.value); success.value = true; }
  catch { error.value = "Couldn't save. Please try again."; }
  finally { saving.value = false; }
}
</script>

<template>
  <section>
    <div class="d-flex align-center justify-space-between mb-4 gap-3 flex-wrap">
      <h1 class="text-h5">Stamp Limits</h1>
      <VBtn @click="openAddDialog">Add Limit</VBtn>
    </div>
    <p v-if="loading">Loading…</p>
    <VCard v-else class="pa-4" max-width="700">
      <VAlert v-if="error" type="error" class="mb-4">{{ error }}</VAlert>
      <VAlert v-if="success" type="success" class="mb-4">Saved successfully.</VAlert>
      <VTable density="compact" class="mb-4">
        <thead>
          <tr>
            <th>Description</th>
            <th>Limit</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, i) in limits" :key="i">
            <td>{{ item.description ?? item.name ?? item.key ?? item.type ?? "—" }}</td>
            <td><VTextField v-model.number="item.limit" type="number" density="compact" hide-details style="width:120px" /></td>
            <td><VBtn size="small" color="error" variant="text" @click="removeLimit(i)">Remove</VBtn></td>
          </tr>
        </tbody>
      </VTable>
      <VBtn :loading="saving" @click="save">Save Changes</VBtn>
    </VCard>

    <VDialog v-model="showAddDialog" max-width="400">
      <VCard>
        <VCardTitle class="pt-4 px-4">Add Limit</VCardTitle>
        <VCardText class="d-flex flex-column gap-3">
          <VTextField v-model="newLimit.description" label="Description" />
          <VTextField v-model.number="newLimit.limit" label="Limit" type="number" />
        </VCardText>
        <VCardActions class="px-4 pb-4 gap-2 justify-end">
          <VBtn variant="tonal" @click="showAddDialog = false">Cancel</VBtn>
          <VBtn :disabled="!newLimit.description" @click="addLimit">Add</VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </section>
</template>
